import type { TFunction } from "i18next";
import websiteImage from "@/assets/images/website-image.webp";
import ericBg from "@/assets/images/eric-bg.webp";

/** Caminho público do favicon (relativo a `src/index.html` no dev/build). */
export const siteFaviconHref = "./assets/favicon.svg";

export type TeaserItem = {
  src: string;
  alt: string;
  caption: string;
  objectPosition: string;
  blurClass: string;
};

export type PrivacySection = { title: string; body: string };

export type HeroScrollSlideKey = "website" | "eric";

export type MediaSlideDef = {
  srcKey: HeroScrollSlideKey;
  objectPosition: string;
  blurClass: string;
};

export type HeroScrollSlide = {
  src: string;
  alt: string;
  caption: string;
  objectPosition: string;
  blurClass: string;
};

/**
 * Configuração central: marca, links de redes e mídias.
 * Textos da interface: `src/locales/*` via react-i18next (`useTranslation`).
 * SEO (title, descrição, Open Graph, Twitter, JSON-LD): `src/index.html`.
 */
export const site = {
  brand: "miiinhau",
  domain: "https://miiinhau.com",
  handle: "@miiinhau",

  /** Rodapé editorial: fuso IANA; textos em i18n (`footer.*`). */
  footer: {
    clockTimeZone: "America/Sao_Paulo",
  },

  links: {
    telegram: "https://t.me/REPLACE_WITH_YOUR_BOT",
    telegramPreviews: "",
    privacyAccess: "https://privacy.com.br/checkout/miiiiinhau",
    twitter: "https://x.com/miiinhau",
    instagram: "",
    homePath: "/",
    designPath: "/design",
  },

  media: {
    heroBackground: websiteImage,
    heroBackgroundPosition: "45% 25%",
    teaser: {
      website: websiteImage,
      eric: ericBg,
    },
  },

  home: {
    heroScroll: {
      slideDefs: [
        {
          srcKey: "website" as const,
          objectPosition: "45% 25%",
          blurClass: "blur-md",
        },
        {
          srcKey: "eric" as const,
          objectPosition: "55% 38%",
          blurClass: "blur-lg",
        },
        {
          srcKey: "website" as const,
          objectPosition: "38% 52%",
          blurClass: "blur-xl",
        },
        {
          srcKey: "eric" as const,
          objectPosition: "52% 62%",
          blurClass: "blur-lg",
        },
        {
          srcKey: "website" as const,
          objectPosition: "48% 35%",
          blurClass: "blur-2xl",
        },
      ] satisfies readonly [
        MediaSlideDef,
        MediaSlideDef,
        MediaSlideDef,
        MediaSlideDef,
        MediaSlideDef,
      ],
    },
    teaser: {
      itemDefs: [
        {
          srcKey: "website" as const,
          objectPosition: "42% 28%",
          blurClass: "blur-lg",
        },
        {
          srcKey: "eric" as const,
          objectPosition: "55% 40%",
          blurClass: "blur-xl",
        },
        {
          srcKey: "website" as const,
          objectPosition: "60% 55%",
          blurClass: "blur-2xl",
        },
        {
          srcKey: "eric" as const,
          objectPosition: "48% 65%",
          blurClass: "blur-lg",
        },
      ] satisfies readonly [MediaSlideDef, MediaSlideDef, MediaSlideDef, MediaSlideDef],
    },
  },
} as const;

const mediaByKey = {
  website: site.media.teaser.website,
  eric: site.media.teaser.eric,
} as const;

/** Itens do grid teaser com `src` e textos resolvidos a partir de i18n. */
export function getTeaserItems(t: TFunction): TeaserItem[] {
  return site.home.teaser.itemDefs.map((item, i) => ({
    src: mediaByKey[item.srcKey],
    alt: t(`home.teaser.items.${i}.alt`),
    caption: t(`home.teaser.items.${i}.caption`),
    objectPosition: item.objectPosition,
    blurClass: item.blurClass,
  }));
}

/** Slides do hero com scroll — `src` e textos resolvidos a partir de i18n. */
export function getHeroScrollSlides(t: TFunction): readonly HeroScrollSlide[] {
  return site.home.heroScroll.slideDefs.map((s, i) => ({
    src: mediaByKey[s.srcKey],
    alt: t(`home.heroScroll.slides.${i}.alt`),
    caption: t(`home.heroScroll.slides.${i}.caption`),
    objectPosition: s.objectPosition,
    blurClass: s.blurClass,
  }));
}
